import {
    LEGAL_CASES_CLICK_TOOLTIP,
    LEGAL_CASES_CONFIRMED_DISPOSED_SUB_LABEL,
    LEGAL_CASES_NO_MATCH_TOOLTIP,
    LEGAL_CASES_PROBABLE_DISPOSED_SUB_LABEL,
    RISK_COLORS,
    RISK_HEADER_KEYS,
    RISK_STATE_CONFIG,
    RegulatoryRiskState,
} from './RiskIndicatorConstants';
import type { LegalCasesRiskState, RegulatoryRiskCell } from './RiskIndicators';
// LegalCases already owns the Filed Against / Filed By toggle and its group ids — reused here
// rather than declaring a parallel direction/group type for this column.
import type { Direction, GroupId } from '../Dashboard/components/DashboardTables/LegalCases/legalCasesType';

export const LEGAL_CASES_KEYS = new Set<string>([RISK_HEADER_KEYS.LEGAL_CASES_KEY, RISK_HEADER_KEYS.LEGAL_CASES_BY_KEY]);

export const LEGAL_CASES_DIRECTION_BY_KEY: Record<string, Direction> = {
    [RISK_HEADER_KEYS.LEGAL_CASES_KEY]: 'filed_against',
    [RISK_HEADER_KEYS.LEGAL_CASES_BY_KEY]: 'filed_by',
};

/**
 * polus may still be on the pre-cut shape (state, confirmedCount, probableCount,
 * confirmedOpenCount) while aica-app has already deployed this column — defaulting the three
 * new counts to 0 in that window would render e.g. "No Open" over a cell that is really "5
 * Probable". probableOpenCount is the discriminator: every cell on the new shape carries it.
 */
export function hasLegalCasesCountsShape(cell?: RegulatoryRiskCell): boolean {
    return cell?.probableOpenCount !== undefined;
}

/**
 * The sub-line shown under Confirmed, Probable and No Open alike: Confirmed Disposed wins
 * whenever it's present; Probable Disposed shows only when there's no Confirmed Disposed count.
 * A cell never shows two sub-lines.
 */
function disposedSubLabel(confirmedDisposed: number, probableDisposed: number): string | undefined {
    if (confirmedDisposed > 0) return LEGAL_CASES_CONFIRMED_DISPOSED_SUB_LABEL(confirmedDisposed);
    if (probableDisposed > 0) return LEGAL_CASES_PROBABLE_DISPOSED_SUB_LABEL(probableDisposed);
    return undefined;
}

/**
 * The five-state Legal Cases cell, per direction (Filed Against / Filed By share one machine).
 * Pure so every branch is unit-testable directly, mirroring resolveRegulatoryBadge
 * (ManagementProfileTab/managementProfileColumnRenderers.tsx).
 *
 * Only call this once hasLegalCasesCountsShape(cell) is true — a legacy-shape cell must render
 * through the existing generic path instead (see getRiskIndicator/getRiskCellStyle/scrollToGroup).
 */
export function resolveLegalCasesState(cell: RegulatoryRiskCell, direction: Direction): LegalCasesRiskState {
    if (cell.state === RegulatoryRiskState.NOT_AVAILABLE) {
        const config = RISK_STATE_CONFIG[RegulatoryRiskState.NOT_AVAILABLE];
        return {
            id: 'NOT_AVAILABLE',
            label: config.label ?? '',
            openable: false,
            tick: config.tick,
            color: config.color,
            backgroundColor: config.backgroundColor,
        };
    }

    const confirmedOpen = cell.confirmedOpenCount ?? 0;
    const confirmedDisposed = cell.confirmedDisposedCount ?? 0;
    const probableOpen = cell.probableOpenCount ?? 0;
    const probableDisposed = cell.probableDisposedCount ?? 0;

    // One rule for the sub-line, shared by every openable state (Confirmed, Probable, No Open):
    // Confirmed Disposed wins whenever it's present; Probable Disposed shows only when there's no
    // Confirmed Disposed count. A cell never shows two sub-lines. Product decision — this used to
    // be Confirmed-Disposed-only outside No Open; Probable Disposed now surfaces in the Confirmed
    // and Probable rows too, not just No Open's own Probable-Disposed branch.
    const subLabel = disposedSubLabel(confirmedDisposed, probableDisposed);

    if (confirmedOpen > 0) {
        return {
            id: 'CONFIRMED',
            label: `${confirmedOpen} Confirmed`,
            subLabel,
            tooltip: LEGAL_CASES_CLICK_TOOLTIP,
            openable: true,
            tick: false,
            color: RISK_COLORS.CONFIRMED.color,
            backgroundColor: RISK_COLORS.CONFIRMED.backgroundColor,
            group: 'OPEN',
        };
    }

    if (probableOpen > 0) {
        return {
            id: 'PROBABLE',
            label: `${probableOpen} Probable`,
            subLabel,
            tooltip: LEGAL_CASES_CLICK_TOOLTIP,
            openable: true,
            tick: false,
            color: RISK_COLORS.PROBABLE.color,
            backgroundColor: RISK_COLORS.PROBABLE.backgroundColor,
            group: 'OTHER',
        };
    }

    // No Open — the muted No-Match text colour and its green tick, sitting on the PROBABLE
    // cell's amber tint (per Figma, the one state where text/icon and tint come from different
    // states).
    const noOpenPalette = {
        color: RISK_STATE_CONFIG[RegulatoryRiskState.NO_RISK].color,
        backgroundColor: RISK_COLORS.PROBABLE.backgroundColor,
    };

    if (confirmedDisposed > 0 || probableDisposed > 0) {
        return {
            id: 'NO_OPEN',
            label: 'No Open',
            subLabel,
            tooltip: LEGAL_CASES_CLICK_TOOLTIP,
            openable: true,
            tick: true,
            ...noOpenPalette,
            // Confirmed Disposed lands on the Disposed section; Probable-only lands on Other Probable.
            group: confirmedDisposed > 0 ? 'DISPOSED' : 'OTHER',
        };
    }

    const noMatchConfig = RISK_STATE_CONFIG[RegulatoryRiskState.NO_RISK];
    return {
        id: 'NO_RISK',
        label: noMatchConfig.label ?? '',
        tooltip: LEGAL_CASES_NO_MATCH_TOOLTIP[direction],
        openable: false,
        tick: noMatchConfig.tick,
        color: noMatchConfig.color,
        backgroundColor: noMatchConfig.backgroundColor,
    };
}

/**
 * The group scrollToGroup lands on for a legal-cases pill click, covering both the resolved
 * five-state cell and the pre-deploy legacy shape (today's exact rule, kept verbatim).
 */
export function resolveLegalCasesGroupId(cell: RegulatoryRiskCell, direction: Direction): GroupId {
    if (hasLegalCasesCountsShape(cell)) {
        return resolveLegalCasesState(cell, direction).group ?? 'OPEN';
    }
    return cell.state === RegulatoryRiskState.PROBABLE ? 'OTHER' : (cell.confirmedOpenCount ?? 0) > 0 ? 'OPEN' : 'DISPOSED';
}
